import React, { useEffect, useState } from 'react';
import { Button, Pagination, Table, Tag, Tooltip } from 'antd';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';

const PAGE_SIZE = 10;

const money = value => Number(value || 0).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const percent = value => `${Number(value || 0).toFixed(2)}%`;

const Delta = ({ value, inverse = false }) => {
  if (value === null || value === undefined) return null;
  const positive = inverse ? value <= 0 : value >= 0;
  return (
    <small className={positive ? 'profit-delta is-positive' : 'profit-delta is-negative'}>
      {value >= 0 ? '▲' : '▼'} {Math.abs(value).toFixed(1)}%
    </small>
  );
};

const MetricCell = ({ record, metric, type = 'money', inverse = false, available = true, showComparison = true }) => {
  if (!available) {
    return <Tooltip title="Nguồn dữ liệu này chưa được đồng bộ"><span className="profit-unavailable">—</span></Tooltip>;
  }
  return (
    <div className="profit-metric-cell">
      <span>{type === 'percent' ? percent(record[metric]) : money(record[metric])}</span>
      {showComparison && record.delta && <Delta value={record.delta[metric]} inverse={inverse} />}
    </div>
  );
};

const rating = value => {
  if (value === 'loss') return <Tag color="error">Loss</Tag>;
  if (value === 'optimize') return <Tag color="warning">Optimize</Tag>;
  return <Tag color="blue">High Profit</Tag>;
};

const headerClass = className => ({
  onHeaderCell: () => ({ className }),
});

const columnTitle = (...lines) => (
  <span className="profit-column-title">{lines.join('\n')}</span>
);

const ProfitabilityTable = ({ title, nameLabel, data, coverage = {}, showComparison = true, showRating = false }) => {
  const [viewMode, setViewMode] = useState('initial');
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setViewMode('initial');
    setCurrentPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [data]);

  const columns = [
    {
      title: columnTitle(nameLabel), dataIndex: 'name', key: 'name', width: 130,
      render: (value, record) => (
        <div className="profit-dimension-cell">
          <strong>{value}</strong>
          <small>{record.ordersCount} đơn / {record.itemsCount} items</small>
        </div>
      )
    },
    { title: columnTitle('Total Revenue'), key: 'totalRevenue', width: 105, align: 'right', render: (_, row) => <MetricCell record={row} metric="totalRevenue" showComparison={showComparison} /> },
    { title: columnTitle('Earning'), key: 'earning', width: 100, align: 'right', ...headerClass('profit-header-earning'), render: (_, row) => <MetricCell record={row} metric="earning" showComparison={showComparison} /> },
    { title: columnTitle('Base Cost'), key: 'productCost', width: 95, align: 'right', render: (_, row) => <MetricCell record={row} metric="productCost" inverse showComparison={showComparison} /> },
    { title: columnTitle('Amazon Ads', 'Cost'), key: 'amazonAdsCost', width: 105, align: 'right', render: (_, row) => <MetricCell record={row} metric="amazonAdsCost" inverse available={coverage.amazonAdsCost} showComparison={showComparison} /> },
    { title: columnTitle('DSP Ads', 'Cost'), key: 'dspAdsCost', width: 95, align: 'right', render: (_, row) => <MetricCell record={row} metric="dspAdsCost" inverse available={coverage.dspAdsCost} showComparison={showComparison} /> },
    { title: columnTitle('FBA Storage', 'Fee'), key: 'fbaStorageFee', width: 105, align: 'right', render: (_, row) => <MetricCell record={row} metric="fbaStorageFee" inverse available={coverage.fbaStorageFee} showComparison={showComparison} /> },
    { title: columnTitle('Inbound', 'Fee'), key: 'inboundFee', width: 90, align: 'right', render: (_, row) => <MetricCell record={row} metric="inboundFee" inverse available={coverage.inboundFee} showComparison={showComparison} /> },
    { title: columnTitle('Refund'), key: 'refund', width: 90, align: 'right', render: (_, row) => <MetricCell record={row} metric="refund" inverse showComparison={showComparison} /> },
    { title: columnTitle('% Refund', 'FBA'), key: 'refundFbaPercent', width: 85, align: 'right', ...headerClass('profit-header-refund-fba'), render: (_, row) => <MetricCell record={row} metric="refundFbaPercent" type="percent" inverse showComparison={showComparison} /> },
    { title: columnTitle('% Refund', 'FBM'), key: 'refundFbmPercent', width: 85, align: 'right', ...headerClass('profit-header-refund-fbm'), render: (_, row) => <MetricCell record={row} metric="refundFbmPercent" type="percent" inverse showComparison={showComparison} /> },
    { title: columnTitle('FBA Fee', 'Adjustment'), key: 'fbaFeeAdjustment', width: 110, align: 'right', ...headerClass('profit-header-adjustment'), render: (_, row) => <MetricCell record={row} metric="fbaFeeAdjustment" showComparison={showComparison} /> },
    { title: columnTitle('Net Margin'), key: 'netMargin', width: 100, align: 'right', className: 'profit-net-column', ...headerClass('profit-header-net-margin'), render: (_, row) => <MetricCell record={row} metric="netMargin" showComparison={showComparison} /> },
    { title: columnTitle('% Net', 'Margin'), key: 'netMarginPercent', width: 90, align: 'right', className: 'profit-net-percent-column', ...headerClass('profit-header-net-percent'), render: (_, row) => <MetricCell record={row} metric="netMarginPercent" type="percent" showComparison={showComparison} /> },
  ];

  if (showRating) {
    columns.push({ title: columnTitle('Đánh giá &', 'khuyến nghị'), key: 'rating', width: 120, align: 'center', render: (_, row) => rating(row.rating) });
  }

  // Ant Table treats numeric widths as pixels. The complete Profit Report has
  // 14-15 columns, so those pixel widths exceed the space left beside the app
  // sidebar and the last columns get clipped. Preserve the intended relative
  // proportions while making every table fit exactly inside its container.
  const totalColumnWeight = columns.reduce((sum, column) => sum + Number(column.width || 1), 0);
  const fittedColumns = columns.map(column => ({
    ...column,
    width: `${(Number(column.width || 1) / totalColumnWeight * 100).toFixed(4)}%`,
  }));
  const rows = data?.rows || [];
  const total = data?.total;
  const hasMultiplePages = rows.length > PAGE_SIZE;
  const displayedRows = viewMode === 'loadMore'
    ? rows.slice(0, visibleCount)
    : rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const canLoadMore = visibleCount < rows.length;

  const resetView = () => {
    setViewMode('initial');
    setCurrentPage(1);
    setVisibleCount(PAGE_SIZE);
  };

  const loadMore = () => {
    setViewMode('loadMore');
    setCurrentPage(1);
    setVisibleCount(count => Math.min(Math.max(count, PAGE_SIZE) + PAGE_SIZE, rows.length));
  };

  const changePage = page => {
    setViewMode('pagination');
    setCurrentPage(page);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <section className="profit-breakdown-block">
      <div className="profit-breakdown-title">{title}</div>
      <div className="profit-breakdown-section">
        <Table
          rowKey="key" size="small" columns={fittedColumns} dataSource={displayedRows} loading={!data}
          tableLayout="fixed"
          pagination={false}
          summary={() => total ? (
            <Table.Summary>
              <Table.Summary.Row className="profit-total-row">
                {fittedColumns.map((column, index) => (
                  <Table.Summary.Cell key={column.key} index={index} align={column.align}>
                    {index === 0 ? <strong>TỔNG CỘNG</strong> : (() => {
                      if (column.key === 'rating') return null;
                      if (coverage[column.key] === false) return <span className="profit-unavailable">—</span>;
                      const isPercent = column.key.toLowerCase().includes('percent');
                      return <strong>{isPercent ? percent(total[column.key]) : money(total[column.key])}</strong>;
                    })()}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          ) : null}
        />
      </div>
      {hasMultiplePages && (
        <div className="profit-table-view-controls">
          {viewMode !== 'loadMore' && (
            <Pagination
              size="small"
              current={currentPage}
              pageSize={PAGE_SIZE}
              total={rows.length}
              showSizeChanger={false}
              showLessItems
              onChange={changePage}
            />
          )}
          <div className="profit-table-view-actions">
            {viewMode !== 'pagination' && (viewMode === 'initial' || canLoadMore) && (
              <Button size="small" type="text" icon={<DownOutlined />} onClick={loadMore}>
                Xem thêm
              </Button>
            )}
            {viewMode !== 'initial' && (
              <Tooltip title="Trở về chế độ mặc định">
                <Button
                  size="small"
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label="Trở về chế độ mặc định"
                  onClick={resetView}
                />
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProfitabilityTable;
